import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getProp from "@insite/mobius/utilities/getProp";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");
    const SignInPageLink = getPageLinkByPageType(state, "SignInPage");
    const currentLocation = getLocation(state);
    return {
        createAccountPageUrl: createAccountPageLink?.url,
        signInPageUrl: SignInPageLink?.url,
        userName: state.context.session.userName,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        currentLocation,
       siteName: state.context.website.name,
    };
};

interface OwnProps extends WidgetProps {
    fields: {};
}

export interface CENProductExploreStyle {
    container?: InjectableCss;
    titleText?: InjectableCss;
    description?: InjectableCss;
    buttonLabel?: InjectableCss;
    signInButton?: InjectableCss;
    subContainer?: InjectableCss;
    SignInButton?: InjectableCss;
}

export const exploreStyles: CENProductExploreStyle = {
    container: {
        css: css`
            padding: 16px;
            background: ${COLORS.background.grayShade};
            width: 100% !important;
            min-height: 80px;
            margin-bottom: 10px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        align-items: center;
                        padding: 24px;
                        gap: 16px;
                        width: 343px;
                        height: 192px;
                    `,
                    null,
                    null,
                    css`
                        width: 612px !important;
                    `,
                    css`
                        width: 612px !important;
                    `,
                ])}
        `,
    },
    subContainer: {
        css: css`
            display: flex;
            padding-top: 8px;
        `,
    },
    titleText: {
        css: css`
            width: 100%;
            padding-right: 14px;
            font-size: ${TYPOGRAPHY.size.base};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: ${TYPOGRAPHY.lineHeight.base};
            color: ${getProp("theme.typography.p.color")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 295px;
                        height: 48px;
                        font-weight: 600;
                        font-size: 16px;
                        line-height: 24px;
                        text-align: center;
                        display: flex;
                        flex-direction: column;
                        margin-bottom: 16px;
                        padding-right: 0;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    description: {
        css: css`
            font-size: ${TYPOGRAPHY.size.tiny};
            font-weight: ${TYPOGRAPHY.weight.base};
            line-height: 16px;
            padding-right: 8px;
            flex: none;
            color: ${getProp("theme.typography.p.color")};
        `,
    },
    signInButton: {
        css: css`
            min-height: 24px !important;
            border-radius: 16px !important;
            padding: 4px 12px;
            width: 69px;
            span {
                font-size: ${TYPOGRAPHY.size.tiny};
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            font-size: ${TYPOGRAPHY.size.large};
                            font-weight: ${TYPOGRAPHY.weight.mediumbold};
                            line-height: ${TYPOGRAPHY.lineHeight.base};
                        `,
                        css`
                            font-size: ${TYPOGRAPHY.size.large};
                            font-weight: ${TYPOGRAPHY.weight.mediumbold};
                            line-height: ${TYPOGRAPHY.lineHeight.base};
                        `,
                        null,
                        null,
                        null,
                    ])}
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: flex;
                        flex-direction: row;
                        padding: 12px 24px !important;
                        width: 115px;
                        height: 48px;
                        border-radius: 24px !important;
                        font-size: 18px;
                        align-items: center;
                        margin: auto;
                        justify-content: center;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    buttonLabel: {
        css: css`
            width: 100%;
            font-size: ${TYPOGRAPHY.size.tiny};
            line-height: 16px;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            color: ${COLORS.link.base};
            cursor: pointer;
            ${({ theme }: { theme: BaseTheme }) => breakpointMediaQueries(theme, [css``, null, null, null, null])}
        `,
    },
};

const styles = exploreStyles;

type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasHistory;

export const CartLoginSection: FC<Props> = ({
    fields,
    createAccountPageUrl,
    signInPageUrl,
    currentLocation,
    userName,
    currentUserIsGuest,
    siteName
}) => {
    const history = useHistory();

    if (!(!userName || (userName && currentUserIsGuest))) {
        return null;
    }

    const onSignInHandler = (e: React.MouseEvent) => {
        if (!signInPageUrl) {
            history.push("/SignIn");
        } else {
            const returnUrl = currentLocation.pathname + currentLocation.search;
            if (returnUrl === "/") {
                history.push(`${signInPageUrl}`);
            } else {
                history.push(`${signInPageUrl}?returnUrl=${encodeURIComponent(returnUrl)}`);
            }
        }
        e.preventDefault();
    };

    const createAccountHandler = (e: React.MouseEvent) => {
        if (!createAccountPageUrl) {
            history.push("/MyAccount/CreateAccount");
        } else {
            const returnUrl = currentLocation.pathname + currentLocation.search;
            if (returnUrl === "/") {
                history.push(`${createAccountPageUrl}`);
            } else {
                history.push(`${createAccountPageUrl}?returnUrl=${encodeURIComponent(returnUrl)}`);
            }
        }
        e.preventDefault();
    };
    const websiteName = siteName === "Nisco" ? "Nisco" : "Centro";
    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper>
                <Typography {...styles.titleText}>
                    {translate(`Already have a ${websiteName} account? Sign in for a faster checkout.`)}
                </Typography>
                <Button {...styles.signInButton} onClick={onSignInHandler}>
                    {translate("Sign In")}
                </Button>
            </StyledWrapper>
            <StyledWrapper {...styles.subContainer}>
                <Typography {...styles.description}>{translate("Don’t have an account?")} </Typography>
                <Link onClick={createAccountHandler} {...styles.buttonLabel}>
                    {translate("Register Now")}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const productExplore: WidgetModule = {
    component: connect(mapStateToProps, null)(CartLoginSection),
    definition: {
        group: "Basic",
        displayName: "CEN Login Section",
        fieldDefinitions: [],
    },
};

export default productExplore;
